"use client";

import React, { useContext, useState } from "react";
import { UserContext } from "@/contexts/UserContext";
import { UserRole } from "@/types/user";
import "./styles.scss";

const isDev = process.env.NODE_ENV === "development";

const ROLE_CONFIG: Record<UserRole, { label: string; color: string; icon: string }> = {
  [UserRole.ADMIN]: {
    label: "Admin",
    color: "#e74c3c",
    icon: "👑",
  },
  [UserRole.EDITOR]: {
    label: "Editor",
    color: "#3498db",
    icon: "✏️",
  },
  [UserRole.VIEWER]: {
    label: "Viewer",
    color: "#2ecc71",
    icon: "👁️",
  },
};

export const DevRoleSelector = () => {
  const userContext = useContext(UserContext);
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't render in production
  if (!isDev || !userContext) {
    return null;
  }

  const { effectiveRole, devRoleOverride, setDevRoleOverride } = userContext;
  const currentConfig = ROLE_CONFIG[effectiveRole];

  const handleRoleChange = (role: UserRole) => {
    setDevRoleOverride(role);
    setIsExpanded(false);
  };

  const handleClearOverride = () => {
    setDevRoleOverride(null);
    setIsExpanded(false);
  };

  return (
    <div className="dev-role-selector">
      <button
        className="dev-role-selector__toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ borderColor: currentConfig.color }}
        title="Dev Role Selector"
      >
        <span className="dev-role-selector__icon">{currentConfig.icon}</span>
        <span className="dev-role-selector__label">{currentConfig.label}</span>
        {devRoleOverride && <span className="dev-role-selector__override-badge">DEV</span>}
      </button>

      {isExpanded && (
        <div className="dev-role-selector__menu">
          <div className="dev-role-selector__header">
            <span className="dev-role-selector__title">🔧 Dev Role Selector</span>
            <button
              className="dev-role-selector__close"
              onClick={() => setIsExpanded(false)}
            >
              ✕
            </button>
          </div>

          <div className="dev-role-selector__roles">
            {Object.values(UserRole).map((role) => {
              const config = ROLE_CONFIG[role];
              const isSelected = effectiveRole === role;

              return (
                <button
                  key={role}
                  className={`dev-role-selector__role ${isSelected ? "dev-role-selector__role--selected" : ""}`}
                  onClick={() => handleRoleChange(role)}
                  style={{
                    borderColor: isSelected ? config.color : "transparent",
                    backgroundColor: isSelected ? `${config.color}15` : undefined,
                  }}
                >
                  <span className="dev-role-selector__role-icon">{config.icon}</span>
                  <div className="dev-role-selector__role-info">
                    <span className="dev-role-selector__role-name">{config.label}</span>
                    <span className="dev-role-selector__role-desc">
                      {role === UserRole.ADMIN && "Full access"}
                      {role === UserRole.EDITOR && "Create & edit QRs"}
                      {role === UserRole.VIEWER && "View only"}
                    </span>
                  </div>
                  {isSelected && <span className="dev-role-selector__check">✓</span>}
                </button>
              );
            })}
          </div>

          {devRoleOverride && (
            <button
              className="dev-role-selector__reset"
              onClick={handleClearOverride}
            >
              Reset to real role
            </button>
          )}

          <div className="dev-role-selector__info">
            <span>💡 This selector is only visible in development mode</span>
          </div>
        </div>
      )}
    </div>
  );
};
